import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

const ARITHMETIC = [
 { op: '+', name: 'Addition', ex: '100 + 50', res: '150' },
 { op: '-', name: 'Subtraction', ex: '100 - 25', res: '75' },
 { op: '*', name: 'Multiplication', ex: '100 * 5', res: '500' },
 { op: '/', name: 'Division', ex: '100 / 4', res: '25' },
 { op: '%', name: 'Modulus (remainder)', ex: '10 % 3', res: '1' },
 { op: '**', name: 'Exponentiation', ex: '2 ** 3', res: '8' },
];

const COMPARISON = [
 { op: '==', name: 'Equal (loose)', ex: '10 == "10"', res: 'true' },
 { op: '===', name: 'Strict equal', ex: '10 === "10"', res: 'false' },
 { op: '!=', name: 'Not equal', ex: '10 != 5', res: 'true' },
 { op: '!==', name: 'Strict not equal', ex: '10 !== "10"', res: 'true' },
 { op: '>', name: 'Greater than', ex: '20 > 10', res: 'true' },
 { op: '<', name: 'Less than', ex: '5 < 10', res: 'true' },
 { op: '>=', name: 'Greater or equal', ex: '10 >= 10', res: 'true' },
 { op: '<=', name: 'Less or equal', ex: '5 <= 10', res: 'true' },
];

export const JSOperators: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Operators &amp; Expressions</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    The tools that let JavaScript calculate, compare, and decide — turning raw values into the business logic behind every app.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="13 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>Imagine an e-commerce app. A customer adds two products to their cart:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const item1 = 1200;\nconst item2 = 800;` }]} defaultLanguage="javascript" id="ops-cart" />
   <Para>Now you need the total, the discount, the tax, and the final payable amount. But on their own, <Code>1200</Code> and <Code>800</Code> are just values — how do we <Mark>add</Mark> them, <Mark>compare</Mark> them, or check if one is greater than another?</Para>
   <Para>That is exactly what operators are for.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>Operators</Mark> are special symbols that perform operations on values and variables. The values they work on are called <Mark>operands</Mark>.</p>
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `const total = 1200 + 800;\n//            ^^^^   ^   ^^^\n//          operand  op  operand` }]} defaultLanguage="javascript" id="ops-operands" />
  </section>

  {/* 3. Why operators exist */}
  <section className="mb-12">
   <SectionHeading n={3}>Why Operators Exist</SectionHeading>
   <Para>Programs constantly perform operations — calculate an order total, compare user permissions, validate credentials, check inventory, apply discounts. Without operators there are no calculations, no comparisons, and no decisions — which means no useful software. Operators are the language&apos;s way of <Mark>manipulating data</Mark>.</Para>
  </section>

  {/* 4. Core idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea</SectionHeading>
   <Para>Think of operators as <Mark>actions</Mark>: variables store data, operators transform it.</Para>
   <div className="my-6 grid grid-cols-2 sm:grid-cols-3 gap-2">
    {[['+', 'Add'], ['-', 'Subtract'], ['*', 'Multiply'], ['/', 'Divide'], ['>', 'Compare'], ['&&', 'Combine conditions']].map(([o, m]) => (
     <div key={o} className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2">
      <span className="font-mono font-bold text-amber-600 dark:text-amber-400 w-6">{o}</span>
      <span className="text-[13px] text-gray-600 dark:text-gray-300">{m}</span>
     </div>
    ))}
   </div>
  </section>

  {/* 5. Expressions */}
  <section className="mb-12">
   <SectionHeading n={5}>Expressions</SectionHeading>
   <Para>An <Mark>expression</Mark> is any piece of code that produces a value. Operators are what create them.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `10 + 20       // expression -> 30\nconst age = 25;  // 25 is an expression\nage > 18      // expression -> true` }]} defaultLanguage="javascript" id="ops-expressions" />
  </section>

  {/* 6. Arithmetic */}
  <section className="mb-12">
   <SectionHeading n={6}>Arithmetic Operators</SectionHeading>
   <Para>Used for mathematical calculations:</Para>
   <div className="my-6 overflow-x-auto">
    <table className="w-full text-sm">
     <thead>
      <tr className="border-b border-gray-200 dark:border-gray-700">
       {['Operator', 'Meaning', 'Example', 'Result'].map((h) => <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">{h}</th>)}
      </tr>
     </thead>
     <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
      {ARITHMETIC.map((r) => (
       <tr key={r.op}>
        <td className="px-4 py-3 font-mono font-bold text-amber-600 dark:text-amber-400">{r.op}</td>
        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r.name}</td>
        <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-400">{r.ex}</td>
        <td className="px-4 py-3 font-mono text-gray-900 dark:text-white">{r.res}</td>
       </tr>
      ))}
     </tbody>
    </table>
   </div>
   <Callout tone="info" title="Modulus is everywhere">The remainder operator <Code>%</Code> powers even/odd checks, pagination, and any cyclic operation.</Callout>
  </section>

  {/* 7. Assignment */}
  <section className="mb-12">
   <SectionHeading n={7}>Assignment Operators</SectionHeading>
   <Para>Assignment stores a value; compound assignment combines an operation with assignment.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `let count = 10;\n\ncount += 5;   // same as count = count + 5  -> 15\ncount -= 2;   // same as count = count - 2  -> 13` }]} defaultLanguage="javascript" id="ops-assign" />
   <div className="flex flex-wrap gap-2">
    {['+=', '-=', '*=', '/=', '%='].map((x) => <Pill key={x}>{x}</Pill>)}
   </div>
  </section>

  {/* 8. Comparison */}
  <section className="mb-12">
   <SectionHeading n={8}>Comparison Operators</SectionHeading>
   <Para>Comparisons always evaluate to <Code>true</Code> or <Code>false</Code>:</Para>
   <div className="my-6 overflow-x-auto">
    <table className="w-full text-sm">
     <thead>
      <tr className="border-b border-gray-200 dark:border-gray-700">
       {['Operator', 'Meaning', 'Example', 'Result'].map((h) => <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">{h}</th>)}
      </tr>
     </thead>
     <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
      {COMPARISON.map((r) => (
       <tr key={r.op}>
        <td className="px-4 py-3 font-mono font-bold text-amber-600 dark:text-amber-400">{r.op}</td>
        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r.name}</td>
        <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-400">{r.ex}</td>
        <td className={`px-4 py-3 font-mono ${r.res === 'true' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{r.res}</td>
       </tr>
      ))}
     </tbody>
    </table>
   </div>
   <Callout tone="warning" title="== vs ===">
    <Code>==</Code> compares after <Mark>type coercion</Mark> (so <Code>10 == "10"</Code> is <Code>true</Code>). <Code>===</Code> compares value <em>and</em> type (so <Code>10 === "10"</Code> is <Code>false</Code>). Prefer <Code>===</Code>.
   </Callout>
  </section>

  {/* 9. Logical */}
  <section className="mb-12">
   <SectionHeading n={9}>Logical Operators</SectionHeading>
   <Para>Used to combine or invert conditions:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const age = 25;\nconst verified = true;\n\nconsole.log(age > 18 && verified); // AND  -> true (both true)\nconsole.log(false || true);        // OR   -> true (at least one)\nconsole.log(!true);                // NOT  -> false (inverts)` }]} defaultLanguage="javascript" id="ops-logical" />
   <div className="my-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
    {[
     { op: '&&', name: 'AND', rule: 'true only if both sides are true' },
     { op: '||', name: 'OR', rule: 'true if at least one side is true' },
     { op: '!', name: 'NOT', rule: 'flips true ↔ false' },
    ].map((l) => (
     <div key={l.op} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
      <div className="font-mono font-bold text-amber-600 dark:text-amber-400">{l.op} <span className="text-gray-400 text-xs font-sans">{l.name}</span></div>
      <div className="text-[13px] text-gray-600 dark:text-gray-400 mt-1">{l.rule}</div>
     </div>
    ))}
   </div>
  </section>

  {/* 10. Increment / decrement */}
  <section className="mb-12">
   <SectionHeading n={10}>Increment &amp; Decrement</SectionHeading>
   <Para>Shorthand for adding or subtracting 1 — extremely common inside loops:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `let count = 5;\ncount++;   // 6\ncount--;   // back to 5` }]} defaultLanguage="javascript" id="ops-incdec" />
  </section>

  {/* 11. Type coercion */}
  <section className="mb-12">
   <SectionHeading n={11}>Type Coercion &amp; Operators</SectionHeading>
   <Para>A famous interview gotcha. The same operands behave differently depending on the operator:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log("5" + 2);  // "52"  -> + concatenates, 2 becomes "2"\nconsole.log("5" - 2);  // 3     -> - forces numeric conversion` }]} defaultLanguage="javascript" id="ops-coercion" />
   <Callout tone="warning" title="Why this happens"><Code>+</Code> is also string concatenation, so it converts the number to a string. <Code>-</Code> has no string meaning, so it converts the string to a number instead.</Callout>
  </section>

  {/* 12. Precedence */}
  <section className="mb-12">
   <SectionHeading n={12}>Operator Precedence</SectionHeading>
   <Para>Operators don&apos;t simply run left to right — some bind tighter than others.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `10 + 5 * 2;     // 20, not 30 — multiplication runs first\n(10 + 5) * 2;   // 30 — parentheses make intent explicit` }]} defaultLanguage="javascript" id="ops-precedence" />
   <Callout tone="tip">When in doubt, add parentheses. They cost nothing and make intent unmistakable.</Callout>
  </section>

  {/* 13. Real world example */}
  <section className="mb-12">
   <SectionHeading n={13}>Real-World Example</SectionHeading>
   <Para>A food-delivery discount, combining arithmetic, comparison, and logical operators:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const orderAmount = 1200;\nconst isPremiumUser = true;\nlet discount = 0;\n\nif (orderAmount > 1000 && isPremiumUser) {\n  discount = 200;\n}\n\nconst finalAmount = orderAmount - discount;\nconsole.log(finalAmount);` }]} defaultLanguage="javascript" id="ops-realworld" />
   <Output>1000</Output>
  </section>

  {/* 14. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={14}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Using == instead of ===">Loose equality&apos;s coercion (<Code>10 == "10"</Code> → <Code>true</Code>) is a classic source of bugs.</Callout>
   <Callout tone="warning" title="Ignoring precedence"><Code>10 + 5 * 2</Code> is <Code>20</Code>, not <Code>30</Code>.</Callout>
   <Callout tone="warning" title="Misreading coercion"><Code>"5" + 2</Code> is <Code>"52"</Code>, not <Code>7</Code>.</Callout>
  </section>

  {/* 15. Interview concepts */}
  <section className="mb-12">
   <SectionHeading n={15}>Interview Concepts</SectionHeading>
   <div className="flex flex-wrap gap-2">
    {['== vs ===', 'What is type coercion?', 'Output of "5" + 2 and "5" - 2', 'Operator precedence', '&& vs ||'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
  </section>

  {/* 16. Mental model */}
  <section className="mb-12">
   <SectionHeading n={16}>Mental Model</SectionHeading>
   <Para>Picture a small workshop:</Para>
   <div className="my-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
    {[
     { k: 'Variables', v: 'Raw materials', emoji: '📦' },
     { k: 'Operators', v: 'Tools', emoji: '🔧' },
     { k: 'Expressions', v: 'Finished product', emoji: '✅' },
    ].map((c) => (
     <div key={c.k} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 text-center">
      <div className="text-2xl mb-1">{c.emoji}</div>
      <div className="text-sm font-bold text-gray-900 dark:text-white">{c.k}</div>
      <div className="text-[13px] text-gray-600 dark:text-gray-400">{c.v}</div>
     </div>
    ))}
   </div>
  </section>

  {/* 17 & 18 */}
  <section className="mb-4">
   <SectionHeading n={17}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Operators perform actions on values and variables, while expressions combine values and operators to produce a result.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Variables store data, operators transform data, and expressions produce results. Together they form the foundation of all business logic in JavaScript applications.
   </Callout>
  </section>
 </div>
);

export default JSOperators;
